import React, { useState, useEffect } from 'react';
import { X, MapPin, Calendar, Clock, IndianRupee, Briefcase, User, Phone, CheckCircle, Star } from 'lucide-react';
import { doc, getDoc, collection, query, where, getDocs, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { cancelJob } from '../../services/jobService';
import RatingInput from '../RatingInput';
// import { useAuth } from '../../contexts/AuthContext'; // AuthContext doesn't exist in frontend pwa yet

// Checking previous file imports. It imported db from '../../config/firebase'.
// It didn't use useAuth. We need current user ID (employer ID) for reviewerId.
// Assuming we can get it from auth context or pass it.
// Let's check where AuthContext is. Usually contexts/AuthContext.
// But wait, the file I read (EmployerJobDetails.jsx) didn't import useAuth.
// I'll assume standard path or try to find it.
// Actually, I can search for AuthContext usage in frontend/src.

const EmployerJobDetails = ({ job, onClose }) => {
    const [isCancelling, setIsCancelling] = React.useState(false);
    const [assignedApplication, setAssignedApplication] = React.useState(null);
    const [submittingRating, setSubmittingRating] = React.useState(false);
    const [existingRating, setExistingRating] = React.useState(null);

    // We rely on job.employerId as the reviewerId since this is the employer's view of their own job
    const employerId = job?.employerId;

    // Derived values - show professional section for allocated, in_progress, or completed jobs
    const showProfessionalSection = ['allocated', 'in_progress', 'completed'].includes(job?.status);
    const isCompleted = job?.status === 'completed';
    const professional = job?.professionalDetails;

    // Fetch assigned application to get OTPs if showing professional section
    React.useEffect(() => {
        const profId = professional?.uid || professional?.id;
        if (job && showProfessionalSection && job.id && profId) {
            const fetchApp = async () => {
                try {
                    // Fetch directly by document ID since we use professionalId as docId
                    // This avoids composite index requirements
                    const appRef = doc(db, 'jobs', job.id, 'applications', profId);
                    const appSnap = await getDoc(appRef);

                    if (appSnap.exists()) {
                        setAssignedApplication(appSnap.data());
                    }
                } catch (err) {
                    console.error("Error fetching application details:", err);
                }
            };
            fetchApp();
        }
    }, [showProfessionalSection, job, professional]);

    // Check for existing rating
    useEffect(() => {
        const profId = professional?.uid || professional?.id;
        if (job && isCompleted && employerId && profId) {
            const checkRating = async () => {
                try {
                    const ratingsRef = collection(db, 'professionals', profId, 'ratings');
                    const q = query(ratingsRef, where('jobId', '==', job.id));
                    const snapshot = await getDocs(q);

                    if (!snapshot.empty) {
                        setExistingRating({ id: snapshot.docs[0].id, ...snapshot.docs[0].data() });
                    }
                } catch (err) {
                    console.error("Error checking existing rating:", err);
                }
            };
            checkRating();
        }
    }, [job, isCompleted, employerId, professional]);

    if (!job) return null;

    // Allow cancellation for pending or allocated jobs
    const canCancel = ['pending', 'allocated'].includes(job.status);

    const handleCancel = async () => {
        if (!window.confirm("Are you sure you want to cancel this job? This action cannot be undone.")) {
            return;
        }

        setIsCancelling(true);
        try {
            await cancelJob(job.id);
            alert("Job cancelled successfully.");
            onClose();
            window.location.reload();
        } catch (error) {
            alert("Failed to cancel job: " + error.message);
        } finally {
            setIsCancelling(false);
        }
    };

    const handleSubmitRating = async (ratingData) => {
        if (!job || !professional) return;
        setSubmittingRating(true);
        try {
            const profId = professional.uid || professional.id;
            const ratingPayload = {
                ...ratingData,
                jobId: job.id,
                reviewerId: employerId,
                reviewerRole: 'employer',
                createdAt: serverTimestamp(),
            };

            await addDoc(collection(db, 'professionals', profId, 'ratings'), ratingPayload);

            setExistingRating(ratingPayload);
            // alert("Rating submitted successfully!"); 
        } catch (error) {
            console.error("Error submitting rating:", error);
            alert("Failed to submit rating. Please try again.");
        } finally {
            setSubmittingRating(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto shadow-2xl">
                {/* Header */}
                <div className="p-6 border-b border-gray-100 flex justify-between items-start sticky top-0 bg-white z-10">
                    <div>
                        <h2 className="text-2xl font-light text-gray-900 mb-1">{job.title}</h2>
                        <span className={`px-3 py-1 rounded-full text-xs font-medium capitalize inline-block
                            ${job.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                                job.status === 'allocated' ? 'bg-blue-100 text-blue-800' :
                                    job.status === 'completed' ? 'bg-green-100 text-green-800' :
                                        'bg-gray-100 text-gray-800'}`}>
                            {job.status}
                        </span>
                    </div>
                    <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full transition-colors">
                        <X size={24} className="text-gray-500" />
                    </button>
                </div>

                <div className="p-6 space-y-8">

                    {/* Job Details */}
                    <div className="grid grid-cols-2 gap-4">
                        <div className="bg-gray-50 p-4 rounded-lg">
                            <div className="flex items-center text-gray-500 mb-2">
                                <Calendar className="w-4 h-4 mr-2" />
                                <span className="text-xs uppercase tracking-wider">Date</span>
                            </div>
                            <p className="font-medium">{job.date}</p>
                        </div>
                        <div className="bg-gray-50 p-4 rounded-lg">
                            <div className="flex items-center text-gray-500 mb-2">
                                <Clock className="w-4 h-4 mr-2" />
                                <span className="text-xs uppercase tracking-wider">Time</span>
                            </div>
                            <p className="font-medium">{job.time}</p>
                        </div>
                        <div className="bg-gray-50 p-4 rounded-lg">
                            <div className="flex items-center text-gray-500 mb-2">
                                <IndianRupee className="w-4 h-4 mr-2" />
                                <span className="text-xs uppercase tracking-wider">Budget</span>
                            </div>
                            <p className="font-medium">₹{job.budget?.value}</p>
                        </div>
                        <div className="bg-gray-50 p-4 rounded-lg">
                            <div className="flex items-center text-gray-500 mb-2">
                                <Briefcase className="w-4 h-4 mr-2" />
                                <span className="text-xs uppercase tracking-wider">Duration</span>
                            </div>
                            <p className="font-medium">{job.duration} Hours</p>
                        </div>
                    </div>

                    <div>
                        <h3 className="text-sm font-medium text-gray-900 mb-3 uppercase tracking-wide">Description</h3>
                        <p className="text-gray-600 leading-relaxed bg-gray-50 p-4 rounded-lg border border-gray-100">
                            {job.description}
                        </p>
                    </div>

                    <div>
                        <h3 className="text-sm font-medium text-gray-900 mb-3 uppercase tracking-wide">Location</h3>
                        <div className="flex items-start text-gray-600 bg-gray-50 p-4 rounded-lg border border-gray-100">
                            <MapPin className="w-5 h-5 mr-3 mt-0.5 text-gray-400 flex-shrink-0" />
                            <span>{job.location?.address}</span>
                        </div>
                    </div>

                    {/* Allocated Professional Section */}
                    {showProfessionalSection && professional && (
                        <div className="border-t border-gray-200 pt-8">
                            <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
                                <CheckCircle className="w-5 h-5 mr-2 text-green-500" />
                                {isCompleted ? 'Professional (Completed)' :
                                    job.status === 'in_progress' ? 'Professional (In Progress)' : 'Allocated Professional'}
                            </h3>
                            <div className="bg-blue-50 border border-blue-100 rounded-xl p-6">
                                <div className="flex items-start space-x-4">
                                    <div className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 overflow-hidden bg-blue-100 border border-blue-200">
                                        {professional.profilePicture?.url ? (
                                            <img
                                                src={professional.profilePicture.url.replace('10.0.2.2', 'localhost')}
                                                alt={professional.displayName}
                                                className="w-full h-full object-cover"
                                            />
                                        ) : (
                                            <User className="w-6 h-6 text-blue-600" />
                                        )}
                                    </div>
                                    <div className="flex-1">
                                        <h4 className="text-lg font-medium text-gray-900 mb-1">
                                            {professional.displayName}
                                        </h4>
                                        <div className="space-y-2">
                                            {professional.phoneNumber && !isCompleted && (
                                                <div className="flex items-center text-gray-700">
                                                    <Phone className="w-4 h-4 mr-2 text-blue-500" />
                                                    <span className="font-medium">{professional.phoneNumber}</span>
                                                </div>
                                            )}

                                            {/* OTP Display - Hide for completed jobs */}
                                            {!isCompleted && assignedApplication && (
                                                <div className="flex space-x-4 mt-2">
                                                    {assignedApplication.startOtp && (
                                                        <div className="bg-white p-2 rounded border border-blue-200">
                                                            <span className="block text-xs text-gray-500 uppercase font-bold tracking-wider">Start OTP</span>
                                                            <span className="block text-lg font-mono font-semibold tracking-widest text-blue-600">{assignedApplication.startOtp}</span>
                                                        </div>
                                                    )}
                                                    {assignedApplication.endOtp && (
                                                        <div className="bg-white p-2 rounded border border-purple-200">
                                                            <span className="block text-xs text-gray-500 uppercase font-bold tracking-wider">End OTP</span>
                                                            <span className="block text-lg font-mono font-semibold tracking-widest text-purple-600">{assignedApplication.endOtp}</span>
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                        <div className="mt-4 flex space-x-3">
                                            {!isCompleted && professional.phoneNumber && (
                                                <a
                                                    href={`tel:${professional.phoneNumber}`}
                                                    className="flex-1 bg-blue-600 text-white py-2 px-4 rounded-lg text-center text-sm font-medium hover:bg-blue-700 transition-colors flex items-center justify-center"
                                                >
                                                    <Phone className="w-4 h-4 mr-2" />
                                                    Call Now
                                                </a>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Rating Section - Only for Completed Jobs */}
                    {isCompleted && (
                        <div className="border-t border-gray-200 pt-8">
                            {existingRating ? (
                                <div className="bg-yellow-50 p-6 rounded-lg border border-yellow-100">
                                    <h4 className="font-bold text-lg text-yellow-800 mb-3 flex items-center">
                                        <Star className="w-5 h-5 mr-2 fill-yellow-600 text-yellow-600" />
                                        You rated this Professional
                                    </h4>
                                    <div className="flex items-center gap-1 mb-3">
                                        {[1, 2, 3, 4, 5].map(star => (
                                            <Star
                                                key={star}
                                                size={20}
                                                className={`${existingRating.rating >= star ? "text-yellow-500 fill-yellow-500" : "text-gray-300"}`}
                                            />
                                        ))}
                                        <span className="ml-2 font-medium text-yellow-900">{existingRating.rating}.0</span>
                                    </div>
                                    {existingRating.pills && existingRating.pills.length > 0 && (
                                        <div className="flex flex-wrap gap-2 mb-3">
                                            {existingRating.pills.map((pill, i) => (
                                                <span key={i} className="px-2 py-1 bg-yellow-100 text-yellow-800 text-xs rounded-full border border-yellow-200">
                                                    {pill}
                                                </span>
                                            ))}
                                        </div>
                                    )}
                                    {existingRating.comment ? (
                                        <p className="text-gray-700 italic border-l-4 border-yellow-200 pl-3 py-1">"{existingRating.comment}"</p>
                                    ) : null}
                                </div>
                            ) : (
                                <RatingInput
                                    role="professional"
                                    onSubmit={handleSubmitRating}
                                    submitting={submittingRating}
                                />
                            )}
                        </div>
                    )}

                    {/* Cancel Button */}
                    {canCancel && (
                        <div className="border-t border-gray-200 pt-6">
                            <button
                                onClick={handleCancel}
                                disabled={isCancelling}
                                className="w-full py-3 bg-red-50 text-red-600 rounded-lg hover:bg-red-100 transition-colors font-medium disabled:opacity-50"
                            >
                                {isCancelling ? 'Cancelling...' : 'Cancel Job'}
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default EmployerJobDetails;
